/**
 * Synthesised game-feel sound effects, driven off observable game state.
 *
 * No binary assets: every cue is a short tone shaped with the Web Audio API.
 * The AudioContext is created lazily on the first user gesture (browsers block
 * audio that starts before one) and resumed there too. A mute toggle is
 * persisted in localStorage and defaults ON — mirroring the small signal
 * modules in src/app/onboarding.ts and src/app/buildMode.ts.
 */
import { createSignal } from "solid-js";

const KEY = "cd-muted";

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false; // no storage (private mode): sound on
  }
}

const [muted, setMutedSignal] = createSignal(read());
export { muted };

export function setMuted(value: boolean): void {
  try {
    localStorage.setItem(KEY, value ? "1" : "0");
  } catch {
    /* ignore */
  }
  setMutedSignal(value);
}

export function toggleMuted(): void {
  setMuted(!muted());
}

// --- audio engine ------------------------------------------------------------

let ctx: AudioContext | null = null;

/**
 * Get (lazily create) the shared AudioContext and nudge it out of the
 * "suspended" state that the browser autoplay policy parks it in until a user
 * gesture. Safe to call from any event handler; returns null where Web Audio is
 * unavailable.
 */
function audio(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/**
 * Prime the audio engine from a user gesture. Call this on the first pointer /
 * key interaction so later, programmatic cues are allowed to play.
 */
export function unlockAudio(): void {
  audio();
}

/** A single shaped tone: an oscillator through a short attack/decay envelope. */
function tone(
  ac: AudioContext,
  opts: {
    freq: number;
    type?: OscillatorType;
    duration: number;
    gain?: number;
    /** End frequency for a glide, defaults to a flat tone. */
    freqTo?: number;
    /** Delay before the tone starts, in seconds. */
    delay?: number;
  },
): void {
  const { freq, type = "sine", duration, gain = 0.18, freqTo, delay = 0 } = opts;
  const start = ac.currentTime + delay;
  const osc = ac.createOscillator();
  const env = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (freqTo !== undefined) osc.frequency.exponentialRampToValueAtTime(freqTo, start + duration);
  // Quick attack, smooth exponential decay — keeps cues soft, never clicky.
  env.gain.setValueAtTime(0.0001, start);
  env.gain.exponentialRampToValueAtTime(gain, start + 0.008);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(env).connect(ac.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

export type SoundCue = "move" | "capture" | "deploy" | "check" | "gameOver";

/** Play a named cue, unless muted or audio is unavailable. */
export function playCue(cue: SoundCue): void {
  if (muted()) return;
  const ac = audio();
  if (!ac) return;
  switch (cue) {
    case "move":
      // Soft, short click.
      tone(ac, { freq: 320, type: "sine", duration: 0.08, gain: 0.12 });
      break;
    case "capture":
      // Sharper, with a downward bite.
      tone(ac, { freq: 540, freqTo: 180, type: "triangle", duration: 0.16, gain: 0.2 });
      break;
    case "deploy":
      // Low, grounded tone.
      tone(ac, { freq: 150, freqTo: 110, type: "sine", duration: 0.22, gain: 0.18 });
      break;
    case "check":
      // Two-note alert.
      tone(ac, { freq: 660, type: "square", duration: 0.1, gain: 0.1 });
      tone(ac, { freq: 880, type: "square", duration: 0.12, gain: 0.1, delay: 0.1 });
      break;
    case "gameOver":
      // Distinct descending three-note flourish.
      tone(ac, { freq: 520, type: "sine", duration: 0.18, gain: 0.16 });
      tone(ac, { freq: 392, type: "sine", duration: 0.18, gain: 0.16, delay: 0.16 });
      tone(ac, { freq: 262, type: "sine", duration: 0.34, gain: 0.18, delay: 0.32 });
      break;
  }
}
